import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import usersService from '../../../services/users.service';
import { showError, showSuccess } from '../../../utils/ecxSwal';
import './EcxUserProfileModal.css';

const getInitials = (userInfo) => {
    const first = userInfo?.firstName?.[0] || '';
    const last = userInfo?.lastName?.[0] || '';
    const initials = `${first}${last}`.trim().toUpperCase();

    return initials || null;
};

const ProfileField = ({ label, icon, error, children }) => (
    <div className="ecx-user-profile-modal__field">
        <label className="ecx-user-profile-modal__field-label">{label}</label>
        <div className="ecx-user-profile-modal__field-input-wrap">
            <span className="ecx-user-profile-modal__field-icon" aria-hidden="true">
                <i className={`bi ${icon}`} />
            </span>
            <div className="flex-grow-1 min-width-0">{children}</div>
        </div>
        {error && <div className="ecx-user-profile-modal__field-error">{error}</div>}
    </div>
);

function EcxUserProfileModal({ show, userInfo, clinicId, userId, onClose, onSaved }) {
    const navigate = useNavigate();

    const {
        register,
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    } = useForm({
        defaultValues: {
            firstName: '',
            lastName: '',
            email: '',
            username: '',
        },
    });

    const [isSaving, setIsSaving] = useState(false);

    const firstName = watch('firstName');
    const lastName = watch('lastName');
    const email = watch('email');
    const username = watch('username');
    const previewName = [firstName, lastName].filter(Boolean).join(' ').trim() || 'Your name';
    const previewInitials = getInitials({ firstName, lastName }) || getInitials(userInfo);

    useEffect(() => {
        if (!show) {
            return undefined;
        }

        const handleEscape = (event) => {
            if (event.key === 'Escape' && !isSaving) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [show, onClose, isSaving]);

    useEffect(() => {
        if (show && userInfo) {
            reset({
                firstName: userInfo.firstName || '',
                lastName: userInfo.lastName || '',
                email: userInfo.email || '',
                username: userInfo.username || '',
            });
        }
    }, [show, userInfo, reset]);

    const onSubmit = async (formData) => {
        setIsSaving(true);
        const usernameChanged = formData.username !== userInfo?.username;

        try {
            const response = await usersService.updateUser(clinicId, userId, {
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                username: formData.username,
            });

            if (usernameChanged) {
                await showSuccess({
                    title: 'Profile Updated',
                    text: 'Your username was changed. You will be signed out and need to log in again with your new username.',
                });
                onClose();
                navigate('/logout');
                return;
            }

            await showSuccess({
                title: 'Profile Updated',
                text: 'Your account details were saved successfully.',
            });
            onSaved?.(response.data);
            onClose();
        } catch (error) {
            console.error(error);
            showError({
                title: 'Save Failed',
                text: 'Unable to update your profile. Please try again.',
            });
        } finally {
            setIsSaving(false);
        }
    };

    if (!show) {
        return null;
    }

    return (
        <>
            <div
                className="ecx-user-profile-modal__backdrop"
                onClick={isSaving ? undefined : onClose}
                aria-hidden="true"
            />

            <div
                className="ecx-user-profile-modal ecx-user-profile-modal__dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="ecx-user-profile-modal-title"
            >
                <div className="ecx-user-profile-modal__panel">
                    <div className="ecx-user-profile-modal__header">
                        <div className="ecx-user-profile-modal__header-main">
                            <span className="ecx-user-profile-modal__header-icon" aria-hidden="true">
                                <i className="bi bi-person-lines-fill" />
                            </span>
                            <div className="min-width-0">
                                <h5 id="ecx-user-profile-modal-title" className="ecx-user-profile-modal__title">
                                    Edit Profile
                                </h5>
                                <p className="ecx-user-profile-modal__subtitle mb-0">
                                    Update your account details
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary ecx-modal__close-btn flex-shrink-0"
                            onClick={onClose}
                            disabled={isSaving}
                            aria-label="Close edit profile"
                        >
                            <i className="bi bi-x-lg" />
                        </button>
                    </div>

                    <form onSubmit={handleSubmit(onSubmit)} className="ecx-user-profile-modal__form">
                        <div className="ecx-user-profile-modal__body ecx-page-scroll">
                            <section className="ecx-user-profile-modal__profile-card">
                                <span className="ecx-user-profile-modal__avatar" aria-hidden="true">
                                    {previewInitials || <i className="bi bi-person-fill" />}
                                </span>
                                <div className="min-width-0">
                                    <h6 className="ecx-user-profile-modal__profile-name">{previewName}</h6>
                                    <p className="ecx-user-profile-modal__profile-email">
                                        @{username || userInfo?.username || 'username'}
                                    </p>
                                    <p className="ecx-user-profile-modal__profile-email mb-0">
                                        {email || userInfo?.email || 'No email provided'}
                                    </p>
                                </div>
                            </section>

                            <section className="ecx-user-profile-modal__intro">
                                <span className="ecx-user-profile-modal__intro-icon" aria-hidden="true">
                                    <i className="bi bi-info-circle" />
                                </span>
                                <div>
                                    <p className="ecx-user-profile-modal__intro-title">Profile information</p>
                                    <p className="ecx-user-profile-modal__intro-text">
                                        Update your name, email, and username here. Role changes require an administrator.
                                        Changing your username will sign you out and require you to log in again.
                                    </p>
                                </div>
                            </section>

                            <div className="ecx-user-profile-modal__form-grid">
                                <ProfileField
                                    label="First Name"
                                    icon="bi-person"
                                    error={errors.firstName?.message}
                                >
                                    <input
                                        type="text"
                                        className={`form-control form-control-sm ${errors.firstName ? 'is-invalid' : ''}`}
                                        autoComplete="given-name"
                                        {...register('firstName', { required: 'First name is required' })}
                                    />
                                </ProfileField>

                                <ProfileField
                                    label="Last Name"
                                    icon="bi-person"
                                    error={errors.lastName?.message}
                                >
                                    <input
                                        type="text"
                                        className={`form-control form-control-sm ${errors.lastName ? 'is-invalid' : ''}`}
                                        autoComplete="family-name"
                                        {...register('lastName', { required: 'Last name is required' })}
                                    />
                                </ProfileField>

                                <ProfileField
                                    label="Username"
                                    icon="bi-at"
                                    error={errors.username?.message}
                                >
                                    <input
                                        type="text"
                                        className={`form-control form-control-sm ${errors.username ? 'is-invalid' : ''}`}
                                        autoComplete="username"
                                        {...register('username', { required: 'Username is required' })}
                                    />
                                </ProfileField>

                                <ProfileField
                                    label="Email"
                                    icon="bi-envelope"
                                    error={errors.email?.message}
                                >
                                    <input
                                        type="email"
                                        className={`form-control form-control-sm ${errors.email ? 'is-invalid' : ''}`}
                                        autoComplete="email"
                                        {...register('email', {
                                            required: 'Email is required',
                                            pattern: {
                                                value: /^\S+@\S+$/i,
                                                message: 'Invalid email address',
                                            },
                                        })}
                                    />
                                </ProfileField>
                            </div>
                        </div>

                        <div className="ecx-user-profile-modal__footer">
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-secondary ecx-modal__close-btn"
                                onClick={onClose}
                                disabled={isSaving}
                            >
                                <i className="bi bi-x-lg me-1" />
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className="btn btn-sm btn-light-primary px-4"
                                disabled={isSaving}
                            >
                                <i className={`bi ${isSaving ? 'bi-arrow-clockwise ecx-refresh-spin' : 'bi-check-lg'} me-1`} />
                                {isSaving ? 'Saving...' : 'Save Changes'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
}

export default EcxUserProfileModal;
